import React, { useState, useEffect, useRef } from 'react';
import { listingTableSx } from './listingTableSx';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Support as SupportIcon,
  AccessTime as HoursIcon
} from '@mui/icons-material';
import { teamContactsAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { canManageTeamType } from '../utils/rolePermissions';
import { FORM_PLACEHOLDERS } from '../constants/formPlaceholders';
import { ListingSortSelect } from './ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';

interface TeamContact {
  id: number;
  team_type: 'SSO' | 'PAM' | 'IGA';
  user_id: string;
  name: string;
  email: string;
  role: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  updated_by_name?: string;
}

interface TeamInfo {
  id: number;
  team_type: 'SSO' | 'PAM' | 'IGA';
  servicenow_queue_name?: string;
  email_dg?: string;
  coverage_hours?: string | null;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  updated_by_name?: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  role: string;
  full_name: string;
}

interface TeamContactsManagementProps {
  userRole: string;
  allowedTeamTypes?: ('SSO' | 'PAM' | 'IGA')[];
  focusTeam?: 'SSO' | 'PAM' | 'IGA';
  autoEditTeamInfo?: boolean;
}

const TeamContactsManagement: React.FC<TeamContactsManagementProps> = ({ 
  userRole, 
  allowedTeamTypes = ['SSO', 'PAM', 'IGA'],
  focusTeam,
  autoEditTeamInfo = false,
}) => {

  const { showSuccess, showError } = useNotification();
  
  const [contacts, setContacts] = useState<TeamContact[]>([]);
  const [teamInfo, setTeamInfo] = useState<{[key: string]: TeamInfo}>({});
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<TeamContact | null>(null);
  const [formData, setFormData] = useState({
    team_type: 'SSO' as 'SSO' | 'PAM' | 'IGA',
    user_id: '',
    role: ''
  });
  const [editingTeamInfo, setEditingTeamInfo] = useState<string | null>(null);
  const [teamInfoFormData, setTeamInfoFormData] = useState({
    servicenow_queue_name: '',
    email_dg: '',
    coverage_hours: ''
  });

  const teamTypes = allowedTeamTypes;
  const deepLinkHandled = useRef<string | null>(null);

  useEffect(() => {
    loadContacts();
    loadUsers();
    loadTeamInfo();
  }, []);

  // Deep-link from Contacts page: select team tab and open edit dialog.
  useEffect(() => {
    if (!focusTeam || !teamTypes.includes(focusTeam)) return;

    const teamTabIndex = teamTypes.indexOf(focusTeam) + 1; // 0 = All Contacts
    setSelectedTab(teamTabIndex);

    if (!autoEditTeamInfo || loading) return;
    if (!canManageTeamType(userRole, focusTeam)) return;

    const key = `${focusTeam}:${autoEditTeamInfo ? 'edit' : 'view'}`;
    if (deepLinkHandled.current === key) return;
    deepLinkHandled.current = key;

    const info = teamInfo[focusTeam];
    setEditingTeamInfo(focusTeam);
    setTeamInfoFormData({
      servicenow_queue_name: info?.servicenow_queue_name || '',
      email_dg: info?.email_dg || '',
      coverage_hours: info?.coverage_hours || '',
    });
  }, [focusTeam, autoEditTeamInfo, loading, teamInfo, userRole, teamTypes]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await teamContactsAPI.getAllContacts();
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load team contacts');
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await teamContactsAPI.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadTeamInfo = async () => {
    try {
      const info: {[key: string]: TeamInfo} = {};
      for (const teamType of teamTypes) {
        const data = await teamContactsAPI.getTeamInfo(teamType);
        if (data) {
          info[teamType] = data;
        }
      }
      setTeamInfo(info);
    } catch (err) {
      console.error('Error loading team info:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleOpenDialog = async (contact?: TeamContact) => {
    await loadUsers();
    if (contact) {
      setEditingContact(contact);
      setFormData({
        team_type: contact.team_type,
        user_id: contact.user_id,
        role: contact.role
      });
    } else {
      setEditingContact(null);
      setFormData({
        team_type: teamTypes[0],
        user_id: '',
        role: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContact(null);
    setFormData({
      team_type: teamTypes[0],
      user_id: '',
      role: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingContact) {
        await teamContactsAPI.updateContact(editingContact.id, formData);
        showSuccess('Team contact updated successfully');
      } else {
        await teamContactsAPI.createContact(formData);
        showSuccess('Team contact created successfully');
      }
      handleCloseDialog();
      loadContacts();
    } catch (err) {
      showError('Failed to save team contact');
      console.error('Error saving contact:', err);
    }
  };

  const handleDelete = async (contact: TeamContact) => {
    if (window.confirm(`Are you sure you want to delete ${contact.name}?`)) {
      try {
        await teamContactsAPI.deleteContact(contact.id);
        showSuccess('Team contact deleted successfully');
        loadContacts();
      } catch (err) {
        showError('Failed to delete team contact');
        console.error('Error deleting contact:', err);
      }
    }
  };

  const handleEditTeamInfo = (teamType: string) => {
    const info = teamInfo[teamType];
    setEditingTeamInfo(teamType);
    setTeamInfoFormData({
      servicenow_queue_name: info?.servicenow_queue_name || '',
      email_dg: info?.email_dg || '',
      coverage_hours: info?.coverage_hours || ''
    });
  };

  const handleCancelTeamInfoEdit = () => {
    setEditingTeamInfo(null);
    setTeamInfoFormData({
      servicenow_queue_name: '',
      email_dg: '',
      coverage_hours: ''
    });
  };

  const handleSaveTeamInfo = async () => {
    if (!editingTeamInfo) return;

    try {
      await teamContactsAPI.updateTeamInfo(editingTeamInfo as 'SSO' | 'PAM' | 'IGA', teamInfoFormData);
      showSuccess('Team information updated successfully');
      handleCancelTeamInfoEdit();
      loadTeamInfo();
    } catch (err) {
      showError('Failed to update team information');
      console.error('Error updating team info:', err);
    }
  };

  const canManageTeam = (teamType: 'SSO' | 'PAM' | 'IGA'): boolean =>
    canManageTeamType(userRole, teamType);

  const getFilteredContacts = () => {
    const rows = selectedTab === 0
      ? contacts
      : contacts.filter(contact => contact.team_type === teamTypes[selectedTab - 1]);
    return sortListing(rows, listingSort);
  };

  const getTeamChipSx = (teamType: 'SSO' | 'PAM' | 'IGA') => {
    switch (teamType) {
      case 'SSO':
        return { bgcolor: '#1565c0', color: '#fff', fontWeight: 700 };
      case 'PAM':
        return { bgcolor: '#ed6c02', color: '#fff', fontWeight: 700 };
      case 'IGA':
        return { bgcolor: '#2e7d32', color: '#fff', fontWeight: 700 };
      default:
        return { bgcolor: '#1565c0', color: '#fff', fontWeight: 700 };
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading team contacts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Team Contacts Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!canManageTeam(teamTypes[0])}
        >
          Add Contact
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="All Contacts" />
            {teamTypes.map((teamType) => (
              <Tab key={teamType} label={`${teamType} Team`} />
            ))}
          </Tabs>
        </Box>

        {/* Team Information Display */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Team Information
          </Typography>
          <Grid container spacing={2}>
            {teamTypes.map((teamType) => (
              <Grid item xs={12} md={4} key={teamType}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={teamType} 
                        size="small"
                        sx={getTeamChipSx(teamType)}
                      />
                      {canManageTeam(teamType) && (
                        <Tooltip title="Edit Team Information">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTeamInfo(teamType)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <SupportIcon color="action" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          ServiceNow Queue:
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        {teamInfo[teamType]?.servicenow_queue_name || 'Not configured'}
                      </Typography>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailIcon color="action" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Team's Email:
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        {teamInfo[teamType]?.email_dg || 'Not configured'}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <HoursIcon color="action" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Coverage hours
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        {teamInfo[teamType]?.coverage_hours || 'Hours not set'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <TableContainer>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <ListingSortSelect value={listingSort} onChange={setListingSort} />
          </Box>
          <Table sx={listingTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Team Type</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredContacts().map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="action" />
                      <Typography variant="body2">{contact.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="action" />
                      <Typography variant="body2">{contact.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkIcon color="action" />
                      <Typography variant="body2">{contact.role}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={contact.team_type} 
                      size="small"
                      sx={getTeamChipSx(contact.team_type)}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Edit Contact">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(contact)}
                          disabled={!canManageTeam(contact.team_type)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Contact">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(contact)}
                          disabled={!canManageTeam(contact.team_type)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {getFilteredContacts().length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No contacts found for the selected team type.
            </Typography>
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Team Contact' : 'Add Team Contact'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Team Type</InputLabel>
                <Select
                  value={formData.team_type}
                  onChange={(e) => setFormData({ ...formData, team_type: e.target.value as 'SSO' | 'PAM' | 'IGA' })}
                  disabled={!canManageTeam(formData.team_type)}
                >
                  {teamTypes.map((teamType) => (
                    <MenuItem key={teamType} value={teamType}>
                      {teamType} Team
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={users}
                getOptionLabel={(option) => `${option.full_name} (${option.email})`}
                value={users.find(user => user.id === formData.user_id) || null}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, user_id: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="User"
                    required
                    placeholder="Search for a user..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">
                        {option.full_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} • {option.role}
                      </Typography>
                    </Box>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  const filter = inputValue.toLowerCase();
                  return options.filter(option =>
                    option.full_name.toLowerCase().includes(filter) ||
                    option.email.toLowerCase().includes(filter) ||
                    option.username.toLowerCase().includes(filter)
                  );
                }}
                noOptionsText="No users found"
                loading={users.length === 0}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingContact ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Info Edit Dialog */}
      <Dialog open={!!editingTeamInfo} onClose={handleCancelTeamInfoEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {editingTeamInfo} Team Information
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ServiceNow Queue Name"
                value={teamInfoFormData.servicenow_queue_name}
                onChange={(e) => setTeamInfoFormData({ ...teamInfoFormData, servicenow_queue_name: e.target.value })}
                placeholder="e.g., SSO-Support-Queue"
                helperText="The ServiceNow queue name for this team"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Distribution Group"
                value={teamInfoFormData.email_dg}
                onChange={(e) => setTeamInfoFormData({ ...teamInfoFormData, email_dg: e.target.value })}
                placeholder={FORM_PLACEHOLDERS.teamDistributionEmail}
                helperText="The email distribution group for this team"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Coverage hours"
                value={teamInfoFormData.coverage_hours}
                onChange={(e) => setTeamInfoFormData({ ...teamInfoFormData, coverage_hours: e.target.value })}
                placeholder="e.g., Weekdays 09:00–17:00 local"
                helperText="When this queue is covered. This is separate from the queue name and the named admins."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTeamInfoEdit}>
            Cancel
          </Button>
          <Button onClick={handleSaveTeamInfo} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamContactsManagement;
